import { fetchGQL } from "@/lib/contentful"
import {
  DEFAULT_PUBLIC_LANG,
  toContentfulLocale,
} from "@/lib/locale/toContentfulLocale"

/** Contentful GraphQL rich text field: `{ json, links? }` */
export type RichTextField = { json?: any; links?: any }

/** Build id → entry/asset maps from a GraphQL `links` object (Contentful docs pattern). */
export function buildRichTextLinkMaps(links: any) {
  const entryHyperlink = new Map<string, any>()
  const assetHyperlink = new Map<string, any>()
  const entryBlock = new Map<string, any>()
  const assetBlock = new Map<string, any>()
  for (const entry of links?.entries?.hyperlink ?? []) {
    if (entry?.sys?.id) entryHyperlink.set(entry.sys.id, entry)
  }
  for (const asset of links?.assets?.hyperlink ?? []) {
    if (asset?.sys?.id) assetHyperlink.set(asset.sys.id, asset)
  }
  for (const entry of links?.entries?.block ?? []) {
    if (entry?.sys?.id) entryBlock.set(entry.sys.id, entry)
  }
  for (const asset of links?.assets?.block ?? []) {
    if (asset?.sys?.id) assetBlock.set(asset.sys.id, asset)
  }
  return { entryHyperlink, assetHyperlink, entryBlock, assetBlock }
}

function walkHyperlinkIds(
  node: any,
  entryIds: Set<string>,
  assetIds: Set<string>,
) {
  if (!node) return
  const id = node?.data?.target?.sys?.id
  if (id) {
    if (node.nodeType === "entry-hyperlink") entryIds.add(id)
    if (node.nodeType === "asset-hyperlink") assetIds.add(id)
  }
  node.content?.forEach((child: any) => walkHyperlinkIds(child, entryIds, assetIds))
}

/** Collect entry/asset ids from inline hyperlinks in a rich text document. */
export function collectHyperlinkIds(json: any) {
  const entryIds = new Set<string>()
  const assetIds = new Set<string>()
  walkHyperlinkIds(json, entryIds, assetIds)
  return { entryIds: [...entryIds], assetIds: [...assetIds] }
}

/** Merge ids from several rich text fields (one fetch for a FAQ layout, etc.). */
export function collectHyperlinkIdsFromFields(
  fields: (RichTextField | null | undefined)[],
) {
  const entryIds = new Set<string>()
  const assetIds = new Set<string>()
  for (const field of fields) {
    if (!field?.json) continue
    const ids = collectHyperlinkIds(field.json)
    ids.entryIds.forEach((id) => entryIds.add(id))
    ids.assetIds.forEach((id) => assetIds.add(id))
  }
  return { entryIds: [...entryIds], assetIds: [...assetIds] }
}

/**
 * Fetch hyperlink targets when the main page query omitted `links.entries.hyperlink`
 * (nested layouts exceed Contentful's complexity cap if inlined).
 */
export async function fetchHyperlinkLinks(
  entryIds: string[],
  assetIds: string[],
  locale: string = DEFAULT_PUBLIC_LANG,
  isPreview = false,
) {
  if (entryIds.length === 0 && assetIds.length === 0) return undefined

  const contentfulLocale = toContentfulLocale(locale)
  const entryList = entryIds.map((id) => `"${id}"`).join(", ")
  const assetList = assetIds.map((id) => `"${id}"`).join(", ")
  const entryLimit = Math.max(entryIds.length, 1)
  const preview = isPreview ? "true" : "false"

  const parts: string[] = []
  if (entryIds.length) {
    const collectionArgs = `where: { sys: { id_in: [${entryList}] } }, locale: "${contentfulLocale}", preview: ${preview}, limit: ${entryLimit}`
    parts.push(`
      pageLongTextCollection(${collectionArgs}) {
        items { __typename sys { id } slug }
      }
      pageGeneralCollection(${collectionArgs}) {
        items { __typename sys { id } slug }
      }
      pageBaseCollection(${collectionArgs}) {
        items { __typename sys { id } slug }
      }
      pageProductPageCollection(${collectionArgs}) {
        items { __typename sys { id } slug }
      }
      productDetailPageCollection(${collectionArgs}) {
        items { __typename sys { id } productName }
      }
      productTrialPageCollection(${collectionArgs}) {
        items { __typename sys { id } slug }
      }`)
  }
  if (assetIds.length) {
    parts.push(`
      assetLinks: assetCollection(
        where: { sys: { id_in: [${assetList}] } }
        locale: "${contentfulLocale}"
        preview: ${preview}
        limit: ${assetIds.length}
      ) {
        items { sys { id } url title }
      }`)
  }

  const result = await fetchGQL("mxp", `query {${parts.join("")}}`, isPreview)
  const data = result?.data ?? {}
  const entries = [
    ...(data.pageLongTextCollection?.items ?? []),
    ...(data.pageGeneralCollection?.items ?? []),
    ...(data.pageBaseCollection?.items ?? []),
    ...(data.pageProductPageCollection?.items ?? []),
    ...(data.productDetailPageCollection?.items ?? []),
    ...(data.productTrialPageCollection?.items ?? []),
  ].filter(Boolean)

  return {
    entries: { hyperlink: entries },
    assets: { hyperlink: data.assetLinks?.items ?? [] },
  }
}

/** Resolve inline hyperlink `links` for one or more fields (fetches only when missing). */
export async function resolveHyperlinkLinks(
  fields: (RichTextField | null | undefined)[],
  locale: string = DEFAULT_PUBLIC_LANG,
  isPreview = false,
) {
  const existing = fields.find((f) => f?.links?.entries?.hyperlink?.length)
  if (existing?.links) return existing.links

  const { entryIds, assetIds } = collectHyperlinkIdsFromFields(fields)
  return fetchHyperlinkLinks(entryIds, assetIds, locale, isPreview)
}
